import React, { useState, useEffect } from 'react';
import { FaCalendarAlt, FaFilter, FaTimes } from 'react-icons/fa';
import './PeriodFilter.css';

/**
 * Composant de filtre par période
 * Permet de filtrer par mois, année ou plage de dates personnalisée
 * 
 * @param {Function} onFilterChange - Callback appelé quand le filtre change
 * @param {Object} initialFilter - Filtre initial (optionnel)
 */
const PeriodFilter = ({ onFilterChange, initialFilter = null }) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;

  const [filterType, setFilterType] = useState(initialFilter?.type || 'all'); // 'all', 'month', 'year', 'custom'
  const [selectedYear, setSelectedYear] = useState(initialFilter?.year || currentYear);
  const [selectedMonth, setSelectedMonth] = useState(initialFilter?.month || currentMonth);
  const [startDate, setStartDate] = useState(initialFilter?.startDate || '');
  const [endDate, setEndDate] = useState(initialFilter?.endDate || '');
  const [isExpanded, setIsExpanded] = useState(false);

  const months = [
    { value: 1, label: 'Janvier' },
    { value: 2, label: 'Février' },
    { value: 3, label: 'Mars' },
    { value: 4, label: 'Avril' },
    { value: 5, label: 'Mai' },
    { value: 6, label: 'Juin' },
    { value: 7, label: 'Juillet' },
    { value: 8, label: 'Août' },
    { value: 9, label: 'Septembre' },
    { value: 10, label: 'Octobre' },
    { value: 11, label: 'Novembre' },
    { value: 12, label: 'Décembre' },
  ];

  // Générer les années (10 ans en arrière + année courante + 2 ans en avant)
  const years = Array.from({ length: 13 }, (_, i) => currentYear - 10 + i);

  // Appliquer le filtre
  const applyFilter = () => {
    let filter = { type: filterType };

    switch (filterType) {
      case 'month':
        filter = {
          ...filter,
          year: selectedYear,
          month: selectedMonth,
          startDate: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`,
          endDate: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${new Date(selectedYear, selectedMonth, 0).getDate()}`,
        };
        break;
      case 'year':
        filter = {
          ...filter,
          year: selectedYear,
          startDate: `${selectedYear}-01-01`,
          endDate: `${selectedYear}-12-31`,
        };
        break;
      case 'custom':
        if (startDate && endDate) {
          filter = {
            ...filter,
            startDate,
            endDate,
          };
        } else {
          alert('Veuillez sélectionner une date de début et une date de fin');
          return;
        }
        break;
      case 'all':
      default:
        filter = { type: 'all', startDate: null, endDate: null };
        break;
    }

    onFilterChange(filter);
    setIsExpanded(false);
  };

  // Réinitialiser le filtre
  const resetFilter = () => {
    setFilterType('all');
    setSelectedYear(currentYear);
    setSelectedMonth(currentMonth);
    setStartDate('');
    setEndDate('');
    onFilterChange({ type: 'all', startDate: null, endDate: null });
    setIsExpanded(false);
  };

  // Appliquer automatiquement quand on change de type
  useEffect(() => {
    if (filterType === 'all') {
      applyFilter();
    }
  }, [filterType]);

  // Obtenir le label du filtre actif
  const getActiveFilterLabel = () => {
    switch (filterType) {
      case 'month':
        return `${months.find(m => m.value === selectedMonth)?.label} ${selectedYear}`;
      case 'year':
        return `Année ${selectedYear}`;
      case 'custom':
        return startDate && endDate ? `${startDate} → ${endDate}` : 'Plage personnalisée';
      case 'all':
      default:
        return 'Toutes les périodes';
    }
  };

  return (
    <div className="period-filter-container">
      {/* Bouton d'activation du filtre */}
      <div className="filter-trigger">
        <button
          className={`filter-btn ${filterType !== 'all' ? 'active' : ''}`}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <FaCalendarAlt style={{ marginRight: '8px' }} />
          <span>{getActiveFilterLabel()}</span>
          <FaFilter style={{ marginLeft: '8px', fontSize: '12px' }} />
        </button>
        {filterType !== 'all' && (
          <button className="reset-btn" onClick={resetFilter} title="Réinitialiser">
            <FaTimes />
          </button>
        )}
      </div>

      {/* Overlay pour mobile */}
      {isExpanded && (
        <div 
          className="filter-overlay" 
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Panneau de configuration du filtre */}
      {isExpanded && (
        <div className="filter-panel">
          <div className="filter-panel-header">
            <h4>Filtrer par période</h4>
            <button className="close-btn" onClick={() => setIsExpanded(false)}>
              <FaTimes />
            </button>
          </div>

          <div className="filter-panel-body">
            {/* Type de filtre */}
            <div className="filter-type-selector">
              <button
                className={`type-btn ${filterType === 'all' ? 'active' : ''}`}
                onClick={() => setFilterType('all')}
              >
                Tout
              </button>
              <button
                className={`type-btn ${filterType === 'month' ? 'active' : ''}`}
                onClick={() => setFilterType('month')}
              >
                Mois
              </button>
              <button
                className={`type-btn ${filterType === 'year' ? 'active' : ''}`}
                onClick={() => setFilterType('year')}
              >
                Année
              </button>
              <button
                className={`type-btn ${filterType === 'custom' ? 'active' : ''}`}
                onClick={() => setFilterType('custom')}
              >
                Personnalisé
              </button>
            </div>

            {/* Options selon le type */}
            {filterType === 'month' && (
              <div className="filter-options">
                <div className="form-group">
                  <label>Mois</label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="form-control"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Année</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="form-control"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {filterType === 'year' && (
              <div className="filter-options">
                <div className="form-group">
                  <label>Année</label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="form-control"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {filterType === 'custom' && (
              <div className="filter-options">
                <div className="form-group">
                  <label>Date de début</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Date de fin</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="form-control"
                    min={startDate}
                  />
                </div>
              </div>
            )}

            {/* Boutons d'action */}
            {filterType !== 'all' && (
              <div className="filter-actions">
                <button className="btn-secondary" onClick={resetFilter}>
                  Réinitialiser
                </button>
                <button className="btn-primary" onClick={applyFilter}>
                  Appliquer
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PeriodFilter;
